import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem("og_chat_session");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("og_chat_session", sid);
  }
  return sid;
};

const GREETING = {
  role: "assistant",
  content: "Hi! I'm the OG Solutions assistant. Ask me anything about our monthly audits, pricing, or how it works.",
};

const renderContent = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const streamingRef = useRef(false);
  streamingRef.current = streaming;

  const loadHistory = async () => {
    if (streamingRef.current) return;
    try {
      const res = await axios.get(`${API}/chat/history/${getSessionId()}`);
      if (res.data.messages.length) {
        setMessages([GREETING, ...res.data.messages.map((m) => ({ role: m.role, content: m.content }))]);
      }
    } catch { /* history load is best-effort */ }
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    loadHistory();
    const t = setInterval(loadHistory, 5000);
    return () => clearInterval(t);
  }, [open]);

  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadContact, setLeadContact] = useState("");
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quote, setQuote] = useState({ name: "", contact: "", budget: "", timeline: "", details: "" });

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/chat/quote`, { session_id: getSessionId(), ...quote });
      setShowQuoteForm(false);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Thanks ${quote.name.split(" ")[0]} — your quote request is in. Owen will get back to you at ${quote.contact} with numbers and next steps. Anything else in the meantime?` },
      ]);
      setQuote({ name: "", contact: "", budget: "", timeline: "", details: "" });
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Something went wrong — you can reach Owen directly at owengraham5546@gmail.com or 705-433-2050." },
      ]);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadContact.trim()) return;
    try {
      await axios.post(`${API}/chat/lead`, {
        session_id: getSessionId(),
        name: leadName,
        contact: leadContact,
      });
      setShowLeadForm(false);
      setLeadName("");
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Got it — Owen will reach out to you directly at ${leadContact}. Anything else I can help with in the meantime?` },
      ]);
      setLeadContact("");
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Something went wrong — you can reach Owen directly at owengraham5546@gmail.com or 705-433-2050." },
      ]);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Something went wrong — please email owengraham5546@gmail.com." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#060D1A] shadow-2xl shadow-black/40 transition-transform duration-300 hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#0B162B]/95 backdrop-blur-xl shadow-2xl shadow-black/50"
          >
            <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-display font-semibold text-white">OG Assistant</p>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Answers in seconds
                </p>
              </div>
            </div>
            <div className="border-b border-white/10 px-4 py-2.5 flex gap-2">
              <button
                onClick={() => { setShowQuoteForm((s) => !s); setShowLeadForm(false); }}
                data-testid="chat-quote-toggle"
                className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]"
              >
                Get a website quote
              </button>
              <button
                onClick={() => { setShowLeadForm((s) => !s); setShowQuoteForm(false); }}
                data-testid="chat-talk-to-human-toggle"
                className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-slate-300 transition-colors duration-300 hover:bg-white hover:text-[#060D1A]"
              >
                Talk to a human
              </button>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-message-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-white text-[#060D1A] rounded-br-md"
                        : m.role === "owner"
                          ? "bg-white/[0.14] text-white rounded-bl-md border border-white/25"
                          : "bg-white/[0.07] text-slate-200 rounded-bl-md"
                    }`}
                  >
                    {m.role === "owner" && (
                      <p className="mb-1 font-mono-label text-[9px] uppercase tracking-[0.2em] text-slate-500">
                        Owen · OG Solutions
                      </p>
                    )}
                    {m.content ? renderContent(m.content) : (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>
            {showQuoteForm && (
              <form onSubmit={submitQuote} className="border-t border-white/10 p-3 space-y-2 bg-white/[0.03]" data-testid="chat-quote-form">
                <input
                  value={quote.name}
                  onChange={(e) => setQuote({ ...quote, name: e.target.value })}
                  placeholder="Your name"
                  required
                  data-testid="quote-input-name"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
                />
                <input
                  value={quote.contact}
                  onChange={(e) => setQuote({ ...quote, contact: e.target.value })}
                  placeholder="Email or phone number"
                  required
                  data-testid="quote-input-contact"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
                />
                <div className="flex gap-2">
                  <select
                    value={quote.budget}
                    onChange={(e) => setQuote({ ...quote, budget: e.target.value })}
                    required
                    data-testid="quote-select-budget"
                    className="flex-1 rounded-xl border border-white/15 bg-[#0B162B] px-3 py-2.5 text-sm text-white outline-none focus:border-white/40"
                  >
                    <option value="">Budget…</option>
                    <option>Under $1,000</option>
                    <option>$1,000 – $2,500</option>
                    <option>$2,500 – $5,000</option>
                    <option>$5,000+</option>
                  </select>
                  <select
                    value={quote.timeline}
                    onChange={(e) => setQuote({ ...quote, timeline: e.target.value })}
                    required
                    data-testid="quote-select-timeline"
                    className="flex-1 rounded-xl border border-white/15 bg-[#0B162B] px-3 py-2.5 text-sm text-white outline-none focus:border-white/40"
                  >
                    <option value="">Timeline…</option>
                    <option>ASAP</option>
                    <option>2–4 weeks</option>
                    <option>1–2 months</option>
                    <option>Just exploring</option>
                  </select>
                </div>
                <textarea
                  value={quote.details}
                  onChange={(e) => setQuote({ ...quote, details: e.target.value })}
                  placeholder="What do you need? (e.g. 5-page site for my plumbing business)"
                  rows={2}
                  data-testid="quote-input-details"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40 resize-none"
                />
                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  className="w-full rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300"
                >
                  Request my quote
                </button>
              </form>
            )}
            {showLeadForm && (
              <form onSubmit={submitLead} className="border-t border-white/10 p-3 space-y-2 bg-white/[0.03]" data-testid="chat-lead-form">
                <input
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="chat-lead-name"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
                />
                <input
                  value={leadContact}
                  onChange={(e) => setLeadContact(e.target.value)}
                  placeholder="Email or phone number"
                  required
                  data-testid="chat-lead-contact"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
                />
                <button
                  type="submit"
                  data-testid="chat-lead-submit"
                  className="w-full rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300"
                >
                  Have Owen contact me
                </button>
              </form>
            )}
            <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pricing, services…"
                className="flex-1 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={streaming || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
                aria-label="Send message"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
